import React, { useEffect, useState } from 'react';
import useInView from './useInView';
import useMotionPrefs from './useMotionPrefs';

/* ==================================================================
   AnimatedCounter
   ------------------------------------------------------------------
   Counts up once the number scrolls into view and re-runs if the
   value itself changes, because dashboard figures usually land after
   first paint. Under reduced motion it prints the final figure.
   ================================================================== */

const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

export default function AnimatedCounter({
  value = 0,
  suffix = '',
  prefix = '',
  duration = 900,
  className = '',
  style,
}) {
  const { reduced } = useMotionPrefs();
  const [ref, inView] = useInView({ threshold: 0.3, skip: reduced });
  const [display, setDisplay] = useState(reduced ? value : 0);

  useEffect(() => {
    if (reduced) {
      setDisplay(value);
      return undefined;
    }
    if (!inView) return undefined;

    let frame = 0;
    let start = 0;

    const tick = (now) => {
      if (!start) start = now;
      const t = Math.min((now - start) / duration, 1);
      setDisplay(Math.round(value * easeOutExpo(t)));
      if (t < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, value, duration, reduced]);

  return (
    <span ref={ref} className={className} style={{ fontVariantNumeric: 'tabular-nums', ...style }}>
      {prefix}
      {display.toLocaleString('en-IN')}
      {suffix}
    </span>
  );
}
